/*
 * NightlyRunAlert · IJ-448 · surfaces the EBTA nightly-refresh health on the
 * Governance Tile. Reads GET /admin/ebta/runs and shows a banner only when
 * attention is needed: amber when no scheduled run is recorded yet, red when a
 * scheduled run exists but is past the staleness window. Silent when fresh, so
 * a healthy tile stays quiet. Every state maps to a measured endpoint value.
 */
import { useEffect, useState } from 'react';
import authedAxios from '../../lib/authedAxios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NightlyRunAlert() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    authedAxios
      .get(`${BACKEND_URL}/api/admin/ebta/runs?limit=5`)
      .then((r) => { if (!cancelled) setInfo(r.data); })
      .catch(() => { if (!cancelled) setInfo(null); });
    return () => { cancelled = true; };
  }, []);

  if (!info) return null;

  const noneYet = !info.last_scheduled_at;
  const overdue = info.scheduled_stale && !!info.last_scheduled_at;
  if (!noneYet && !overdue) return null; // nightly is fresh — stay quiet

  const tone = overdue
    ? { border: 'border-red-500/50', bg: 'bg-red-950/40', dot: 'bg-red-400', text: 'text-red-200' }
    : { border: 'border-amber-500/40', bg: 'bg-amber-950/30', dot: 'bg-amber-400', text: 'text-amber-200' };

  const lastRun = info.last_scheduled_at
    ? new Date(info.last_scheduled_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div
      data-testid="govtile-nightly-alert"
      className={`mt-4 flex items-center gap-3 rounded-lg border ${tone.border} ${tone.bg} px-4 py-2.5`}
    >
      <span className={`h-2.5 w-2.5 flex-none rounded-full ${tone.dot}`} />
      <p className={`text-sm ${tone.text}`}>
        {overdue ? (
          <>
            <b data-testid="govtile-nightly-overdue">Nightly Atlas refresh overdue.</b>{' '}
            Last scheduled run {lastRun} · {info.hours_since_last_scheduled}h ago. Expected every 02:00 EST (07:00 UTC).
          </>
        ) : (
          <>
            <b data-testid="govtile-nightly-none">Nightly Atlas refresh: none recorded yet.</b>{' '}
            The first scheduled run fires at 02:00 EST (07:00 UTC).
          </>
        )}
      </p>
    </div>
  );
}
